import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/Button";
import { Drawer } from "../ui/Overlay";
import { StatusTag } from "../ui/Tag";
import type { Snapshot, Version } from "../../lib/version";
import { fetchAudit, type AuditEntry } from "../../lib/api";
import { useIdolRoster } from "../../lib/useIdolRoster";
import { pickText } from "../../lib/locales";
import { useI18n, useT } from "../../lib/i18n";
import { contentLogForBlocks, groupSessions, type ContentNotice, type NetChange, type Session } from "../../lib/version-log";
import { useChangeLabels } from "./changeLabels";
import { changeNames } from "../../lib/change-names";
import { VersionPeople } from "./VersionPeople";
import { UndoChanges } from "./UndoChanges";
import type { ConfigModuleRestore, ConfigUndoSelection } from "../../lib/api";
import { CONFIG_MODULES, configModule, type ConfigModuleId } from "../../../../src/domain/config-modules";
import { describeAction } from "../../lib/audit-label";

/**
 * 版本修改记录（设计稿 nav.jsx 的 VersionLogDrawer）。
 *
 * 像 git log：一条一个编辑会话（同一个人几分钟内连着存的合成一条），抬头 人 · 时间段 · 改了几处，
 * 收起时一句摘要，点开是改动表 位置 / 字段 / 改前 / 改后，改前红删除线、改后绿。
 * 读取自动保存、后台写入和同步的配置内容记录；建版本、发布这些在顶栏「操作日志」里，底部给入口。
 */

const hm = (ms: number, withDate: boolean) => {
  const d = new Date(ms), p = (n: number) => String(n).padStart(2, "0");
  return (withDate ? `${p(d.getMonth() + 1)}-${p(d.getDate())} ` : "") + `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

type UndoLogRow = NetChange & { receiptIds?: number[] };

export function SessionCard({ s, open, onToggle, snapshot, onUndo }: { s: Session; open: boolean; onToggle: () => void; snapshot?: Snapshot; onUndo?: (selection: ConfigUndoSelection) => void }) {
  const t = useT();
  const sourceIds = s.entries.flatMap((e) => e.id ? [e.id] : []);
  const { where, field, side, cellValue, summary } = useChangeLabels(snapshot, true);
  const [showSaves, setShowSaves] = useState(false);
  const [rowPage, setRowPage] = useState(0);
  useEffect(() => { setRowPage(0); }, [showSaves]);
  const rows: UndoLogRow[] = showSaves || s.reverted ? s.entries.flatMap((e) => e.changes.map((c) => ({ ...c, receiptIds: e.id ? [e.id] : [] }))) : s.rows;
  const page = Math.min(rowPage, Math.max(0, Math.ceil(rows.length / 100) - 1));
  const first = rows[0];
  const preview = first ? summary(first) : null;
  const noticeText = (n: ContentNotice) => {
    if (n.kind === "sync") return n.sourceA0 && n.sourceVersion ? t("vlog.syncSource", { a0: n.sourceA0, version: n.sourceVersion }) : t("vlog.syncSummary");
    const d = describeAction(n.action, () => false);
    return d?.kind === "route" && d.res ? `${t(d.verb)} · ${t(d.res)}` : t("vlog.writeSummary");
  };
  // 同一行的几处改动排在一起，位置只写一次（合并单元格）；按块 + 行 id 分组，不按显示名：
  // 两关的奖励都叫「New Reward」时按名字分会并成一格，看着像同一条奖励改了两次
  const groups = new Map<string, UndoLogRow[]>();
  for (const c of rows.slice(page * 100, (page + 1) * 100)) { const k = `${c.block}|${/^\[[^\]]+\]/.exec(c.path)?.[0] ?? where(c)}`; groups.set(k, [...(groups.get(k) ?? []), c]); }
  const sameDay = new Date(s.fromMs).toDateString() === new Date(s.toMs).toDateString();
  const span = s.fromMs === s.toMs ? hm(s.fromMs, true) : `${hm(s.fromMs, true)} – ${hm(s.toMs, !sameDay)}`;
  const cell = "border-b border-line-lighter px-2 py-1.5 align-top";
  return (
    <div className="mb-2.5 rounded-base border border-line-lighter">
      <button type="button" onClick={onToggle} className="block w-full px-3 py-2.5 text-left hover:bg-fill-light" aria-expanded={open}>
        <div className="flex items-baseline gap-2.5 text-sm">
          <span className={`text-[10px] text-ink-placeholder ${open ? "" : "-rotate-90"}`}>▾</span>
          <span className="font-semibold">{s.actor || t("auditChange.unknownActor")}</span>
          <span className="tabular-nums text-ink-secondary">{span}</span>
          <span className="ml-auto text-xs text-ink-placeholder">{rows.length ? t("vlog.count", { n: rows.length, saves: s.saves }) : t("vlog.summaryCount", { n: s.notices.length, saves: s.saves })}</span>
        </div>
        {!open && first && (
          <div className="mt-1 truncate pl-[18px] text-sm text-ink-regular">
            {where(first)} · {preview?.field}{first.op ? ` · ${t(first.op === "add" ? "vlog.addRow" : "vlog.removeRow")}` : `：${preview?.from} → ${preview?.to}`}
            {rows.length > 1 ? t("vlog.andMore", { n: rows.length }) : ""}
          </div>
        )}
        {!open && !first && s.notices[0] && <div className="mt-1 truncate pl-[18px] text-sm text-ink-regular">{noticeText(s.notices[0])}</div>}
      </button>
      {s.entries.filter((e) => e.undo).map((e, i) => <div key={e.id ?? i} className="px-3 py-2 text-sm text-ink-secondary">{e.undo?.module && configModule(e.undo.module) ? t("module.historySource", { module: t(configModule(e.undo.module)!.label), version: e.undo.sourceVersion ?? "" }) : <>{t("undo.record")} · {t("undo.sources", { ids: `${e.undo?.sourceVersion ? `v${e.undo.sourceVersion} · ` : ""}${(e.undo?.sourceIds ?? []).map((id) => `#${id}`).join(", ")}` })}</>}</div>)}
      {open && onUndo && sourceIds.length > 0 && <div className="px-3 py-2"><Button size="sm" onClick={() => onUndo({ ids: sourceIds })}>{t("undo.session")}</Button></div>}
      {open && s.reverted && <div className="border-t border-line-lighter px-3 py-2 text-sm text-ink-secondary">{t("auditChange.reverted")}</div>}
      {open && s.entries.length > 1 && !s.reverted && <button type="button" className="px-3 py-2 text-sm text-primary" onClick={() => setShowSaves(!showSaves)}>{t(showSaves ? "auditChange.showNet" : "auditChange.showSaves")}</button>}
      {open && s.notices.length > 0 && <ul className="space-y-2 border-t border-line-lighter px-3 py-2.5 text-sm">
        {s.notices.map((n, i) => <li key={i}><div>{noticeText(n)}</div><div className="mt-0.5 text-xs text-ink-placeholder">{t("vlog.noFieldDetails")}</div></li>)}
      </ul>}
      {open && rows.length > 0 && (
        <table className="w-full table-fixed border-collapse text-sm">
          <colgroup><col className="w-[24%]" /><col className="w-[18%]" /><col className="w-[29%]" /><col className="w-[29%]" /></colgroup>
          <thead>
            <tr className="bg-fill-light text-left text-ink-secondary">
              <th className={`${cell} whitespace-nowrap`}>{t("vlog.where")}</th><th className={`${cell} whitespace-nowrap`}>{t("vlog.field")}</th>
              <th className={`${cell} whitespace-nowrap`}>{t("vlog.before")}</th><th className={`${cell} whitespace-nowrap`}>{t("vlog.after")}</th>
            </tr>
          </thead>
          <tbody>
            {[...groups.entries()].flatMap(([loc, cs]) => cs.map((c, i) => (
              <tr key={`${loc}-${i}`} className={c.op === "add" ? "bg-success-50" : c.op === "remove" ? "bg-danger-50" : ""}>
                {i === 0 && <td rowSpan={cs.length} className={`${cell} bg-white font-medium text-ink-regular`}>{where(cs[0])}</td>}
                <td className={cell}>{field(c)}{onUndo && (c.receiptIds ?? sourceIds).length > 0 && <button type="button" className="mt-1 block text-xs text-primary" onClick={() => onUndo({ ids: c.receiptIds ?? sourceIds, field: { block: c.block, path: c.path } })}>{t("undo.item")}</button>}</td>
                {/* 新增和删除也展示内容，避免有名称的整行只剩一个名字。 */}
                <td className={`${cell} break-all text-danger ${c.op ? "" : "line-through"}`}>{c.op === "add" ? "" : cellValue(side(c, "from"), c.row, side(c, "to"))}</td>
                <td className={`${cell} break-all font-semibold text-success`}>{c.op === "remove" ? "" : cellValue(side(c, "to"), c.row, side(c, "from"))}</td>
              </tr>
            )))}
            {s.truncated && <tr><td colSpan={4} className={`${cell} text-ink-placeholder`}>{t("log.truncated")}</td></tr>}
          </tbody>
        </table>
      )}
      {open && rows.length > 100 && <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm text-ink-secondary">
        <span>{t("auditChange.shownRange", { from: page * 100 + 1, to: Math.min((page + 1) * 100, rows.length), total: rows.length })}</span>
        <select aria-label={t("auditChange.changePage")} value={page} onChange={(e) => setRowPage(Number(e.target.value))}>
          {Array.from({ length: Math.ceil(rows.length / 100) }, (_, n) => <option key={n} value={n}>{n + 1}</option>)}
        </select>
        <Button size="sm" disabled={page === 0} onClick={() => setRowPage(page - 1)}>{t("auditChange.previousChanges")}</Button>
        <Button size="sm" disabled={(page + 1) * 100 >= rows.length} onClick={() => setRowPage(page + 1)}>{t("auditChange.nextChanges")}</Button>
      </div>}
    </div>
  );
}

/**
 * 修改记录抽屉（设计稿 nav.jsx VersionLogDrawer）。范围两档：
 * 「这一版」只列这一版的会话；「全部版本」按版本分段（新的在上），每段抬头 版本号 · 状态 · 备注，点版本号切过去。
 * 「某人改了什么」= 全部版本 + 只看他：这时按人到服务端筛（操作日志 actor），翻页才不会被别人的记录占满。
 * 人名后面带他在已加载记录里改了几处。筛到某人时给「在操作日志里看」，带上建版本、发布这些非内容操作
 */
export function VersionLogDrawer({ version, versions = [], initWho = null, initScope = "one", initGroup = "person", onClose, onOpenFull, onPickVersion, blocks, loadSnapshot, onCompare, undoBlocked = false, onUndoApplied }: {
  undoBlocked?: boolean;
  onUndoApplied?: (version: number) => void;
  loadSnapshot?: (id: string) => Promise<Snapshot>;
  onCompare?: (v: Version) => void;
  blocks?: readonly string[];
  version: number;
  /** 用来给「全部版本」的分段抬头取状态与备注 */
  versions?: Version[];
  initWho?: string | null;
  initScope?: "one" | "all";
  initGroup?: "person" | "module";
  onClose: () => void;
  /** 看完整操作日志；筛到某人时带上他 */
  onOpenFull: (who: string | null, scope: "one" | "all") => void;
  onPickVersion?: (id: string) => void;
}) {
  const t = useT();
  const undoTarget = (source: number) => {
    const own = versions.find((v) => v.id === `v${source}`);
    if (!own) return null;
    if (own.status === "draft") return source;
    const descends = (draft: Version) => {
      let base = draft.base; const visited = new Set<string>();
      while (base && !visited.has(base)) { if (base === `v${source}`) return true; visited.add(base); base = versions.find((v) => v.id === base)?.base; }
      return false;
    };
    const draft = versions.find((v) => v.status === "draft" && v.base === `v${source}`) ?? versions.find((v) => v.status === "draft" && descends(v));
    return draft ? Number(draft.id.slice(1)) : null;
  };
  const openUndo = (source: number, selection: ConfigUndoSelection) => {
    const target = undoTarget(source);
    if (target != null) setUndo({ version: target, selection: { ...selection, ...(target !== source ? { sourceVersion: source } : {}), ...(visibleBlocks ? { blocks: [...visibleBlocks] } : {}) } });
  };
  const [undo, setUndo] = useState<{ version: number; selection: ConfigUndoSelection | ConfigModuleRestore } | null>(null);
  const [groupBy, setGroupBy] = useState<"person" | "module">(initGroup);
  const modules = CONFIG_MODULES.filter((m) => !blocks || m.blocks.some((b) => blocks.includes(b)));
  const [moduleId, setModuleId] = useState<ConfigModuleId>(modules[0]?.id ?? "talent");
  const selectedModule = modules.find((m) => m.id === moduleId) ?? modules[0];
  const visibleBlocks = groupBy === "module" ? selectedModule?.blocks : blocks;
  const [refresh, setRefresh] = useState(0);
  const [scope, setScope] = useState<"one" | "all">(initScope);
  const [who, setWho] = useState<string | null>(initWho);
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  /** 服务端已经翻过的条数（按人精确收之前）：判断还有没有更早的记录要看它，不看收过之后的 items */
  const [fetched, setFetched] = useState(0);
  const [page, setPage] = useState(1);
  const [state, setState] = useState<"loading" | "idle" | "error">("loading");
  const [open, setOpen] = useState<Set<string>>(new Set(["0-0"]));
  // 全部版本按人筛走服务端：换人就从第一页重来；这一版的人名筛在本地做
  const serverWho = groupBy === "person" ? who : null;
  useEffect(() => { setPage(1); setItems([]); setOpen(new Set(["0-0"])); }, [scope, serverWho, version]);
  useEffect(() => {
    let alive = true;
    setState("loading");
    const q = scope === "one" ? { version, actor: serverWho ?? undefined, page, pageSize: 100 } : { configOnly: true, actor: serverWho ?? undefined, page, pageSize: 100 };
    fetchAudit(q)
      .then((d) => {
        if (!alive) return;
        // actor 是模糊匹配，sion 会带出 sion2；这里按名字精确收一次
        const got = serverWho ? d.items.filter((x) => x.actor === serverWho) : d.items;
        setItems((xs) => (page === 1 ? got : [...xs, ...got])); setFetched((n) => (page === 1 ? 0 : n) + d.items.length); setTotal(d.total); setState("idle");
      })
      .catch(() => { if (alive) setState("error"); });
    return () => { alive = false; };
  }, [scope, serverWho, version, page, refresh]);
  const loader = useRef(loadSnapshot); loader.current = loadSnapshot;
  const [snapshots, setSnapshots] = useState<Record<number, Snapshot>>({});
  const versionKey = scope === "one" ? String(version) : [...new Set(items.map((e) => e.configVersion).filter((v) => v != null))].join(",");
  useEffect(() => {
    let alive = true;
    const pending = versionKey.split(",").filter(Boolean).map(Number).filter((v) => !snapshots[v]);
    let next = 0;
    void Promise.all([0, 1].map(async () => {
      while (alive && next < pending.length && loader.current) {
        const v = pending[next++];
        try { const s = await loader.current(`v${v}`); if (alive) setSnapshots((old) => ({ ...old, [v]: s })); }
        catch { /* Missing names keep their identifier; audit rows remain visible. */ }
      }
    }));
    return () => { alive = false; };
    // Snapshots only supply names; changing pages must not restart successful reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionKey, refresh]);
  const { lang } = useI18n();
  const { idols } = useIdolRoster(items.some((e) => Array.isArray(e.detail?.changes) && e.detail.changes.some((c: { block?: string }) => c?.block === "stages" || c?.block === "stageImages")));
  const idolName = (id: string) => { const idol = idols.find((i) => i.idolId === id); return idol ? pickText(idol.stageNameI18n, lang) : id; };
  const entriesFor = (xs: AuditEntry[], v: number) => contentLogForBlocks(xs, visibleBlocks).map((e) => ({ ...e, changes: changeNames(e.changes, snapshots[v], t, idolName) }));
  const groups = useMemo(() => {
    if (scope === "one") return [{ v: version, sessions: groupSessions(entriesFor(items, version)) }];
    const byV = new Map<number, AuditEntry[]>();
    for (const x of items) if (x.configVersion != null) byV.set(x.configVersion, [...(byV.get(x.configVersion) ?? []), x]);
    return [...byV.entries()].sort((a, b) => b[0] - a[0]).map(([v, xs]) => ({ v, sessions: groupSessions(entriesFor(xs, v)) }));
  }, [scope, version, items, visibleBlocks, snapshots, t, idols, lang]);
  const toggle = (k: string) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const count = new Map<string, number>();
  for (const g of groups) for (const s of g.sessions) count.set(s.actor || t("auditChange.unknownActor"), (count.get(s.actor || t("auditChange.unknownActor")) ?? 0) + Math.max(s.rows.length, s.notices.length, s.reverted ? s.entries.flatMap((e) => e.changes).length : 0));
  const names = [...new Set([...(scope === "one" ? versions.find((v) => v.id === `v${version}`)?.people?.map((p) => p.actor) ?? [] : []), ...count.keys()])];
  // 切换筛选后 items 暂时为空；版本人员里已有 who 时不能再次插入，否则重复 key 会留下旧按钮。
  if (who && !names.includes(who)) names.unshift(who);
  const shown = groups.map((g) => ({ ...g, sessions: serverWho ? g.sessions.filter((s) => (s.actor || t("auditChange.unknownActor")) === serverWho) : g.sessions })).filter((g) => g.sessions.length);
  const base = scope === "all" ? t("vlog.titleAll") : t("ver.versionLogTitle", { id: `v${version}` });
  const title = serverWho ? t("vlog.titleWho", { who: serverWho, title: base }) : base;
  const seg = (on: boolean) => `px-2.5 py-0.5 text-sm ${on ? "bg-primary text-white" : "bg-white text-ink-regular hover:bg-fill-light"}`;
  const empty = fetched < total ? t("vlog.noneLoaded") : t("auditChange.noHistory");
  return (
    <Drawer title={title} width={760} onClose={onClose} extra={<Button onClick={onClose}>{t("log.close")}</Button>}>
      {scope === "one" && versions.find((v) => v.id === `v${version}`) && <div className="mb-3 space-y-2 rounded-base border border-line-lighter p-3">
        <VersionPeople v={versions.find((v) => v.id === `v${version}`)!} onPerson={(name) => { setWho(name); setGroupBy("person"); }} />
        {onCompare && <div><Button size="sm" onClick={() => onCompare(versions.find((v) => v.id === `v${version}`)!)}>{t("auditChange.compareSource")}</Button></div>}
      </div>}
      {onUndoApplied && scope === "one" && undoTarget(version) !== version && <p className="mb-3 text-sm text-ink-secondary">{undoTarget(version) == null ? t("undo.needDraft") : t("undo.targetDraft", { version: undoTarget(version)! })}</p>}
      {blocks && <div className="mb-2 text-sm text-ink-secondary">{t("talentPage.pageLog")}</div>}
      <div className="mb-2 text-xs text-ink-placeholder">{t("auditChange.nameReference")}</div>
      <div role="tablist" aria-label={t("module.grouping")} className="mb-3 flex gap-5 border-b border-line">
        {(["person", "module"] as const).map((id) => <button key={id} type="button" role="tab" aria-selected={groupBy === id} className={`border-b-2 py-2 text-sm ${groupBy === id ? "border-primary text-primary" : "border-transparent text-ink-secondary"}`} onClick={() => { setGroupBy(id); setOpen(new Set(["0-0"])); }}>{t(id === "person" ? "module.byPerson" : "module.byModule")}</button>)}
      </div>
      {groupBy === "module" && selectedModule && <div className="mb-3 space-y-3">
        <select aria-label={t("module.select")} className="rounded-base border border-line bg-white px-3 py-1.5 text-sm" value={selectedModule.id} onChange={(e) => { setModuleId(e.target.value as ConfigModuleId); setOpen(new Set(["0-0"])); }}>
          {modules.map((m) => <option key={m.id} value={m.id}>{t(m.label)}</option>)}
        </select>
        {scope === "one" && onUndoApplied && versions.find((v) => v.id === `v${version}`)?.status === "draft" && <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={undoBlocked} onClick={() => setUndo({ version, selection: { module: selectedModule.id, source: "base" } })}>{t("module.undoAll")}</Button>
          <Button size="sm" disabled={undoBlocked} onClick={() => setUndo({ version, selection: { module: selectedModule.id, source: "active" } })}>{t("module.useActive")}</Button>
        </div>}
        <p className="text-xs text-ink-secondary">{t("module.scope")}</p>
      </div>}
      {state === "error" && <div className="mb-3 text-sm text-danger">{t("vlog.loadFail")}</div>}
      <div className="mb-2.5 flex flex-wrap items-center gap-2 text-sm">
        <span className="inline-flex overflow-hidden rounded-base border border-primary">
          <button type="button" aria-pressed={scope === "one"} className={seg(scope === "one")} onClick={() => setScope("one")}>{t("vlog.scopeOne")}</button>
          <button type="button" aria-pressed={scope === "all"} className={seg(scope === "all")} onClick={() => setScope("all")}>{t("vlog.scopeAll")}</button>
        </span>
        {groupBy === "person" && names.length > 0 && <span className="text-ink-secondary">{t("vlog.onlyWho")}</span>}
        {groupBy === "person" && names.map((n) => (
          <button key={n} type="button" aria-pressed={who === n} onClick={() => { setWho(who === n ? null : n); setOpen(new Set(["0-0"])); }}
            className={`rounded-full border px-2 py-0.5 ${who === n ? "border-primary text-primary" : "border-line text-ink-regular hover:border-primary"}`}>
            {n}{count.has(n) && <span className="ml-1 text-ink-placeholder">{count.get(n)}</span>}
          </button>
        ))}
        {serverWho && scope === "one" && onUndoApplied && undoTarget(version) != null && <Button size="sm" onClick={() => openUndo(version, { actor: serverWho })}>{t(blocks ? "undo.personPage" : "undo.person")}</Button>}
        {serverWho && <button type="button" className="ml-auto text-primary hover:underline" onClick={() => onOpenFull(serverWho, scope)}>{t("vlog.auditWho", { who: serverWho })}</button>}
      </div>
      {state === "idle" && !shown.length && <div className="py-6 text-center text-sm text-ink-placeholder">{empty}</div>}
      {shown.map((g, gi) => {
        const info = versions.find((v) => v.id === `v${g.v}`);
        return (
          <div key={g.v}>
            {scope === "all" && (
              <div className={`mb-2 flex items-center gap-2 text-sm ${gi ? "mt-4" : "mt-1"}`}>
                <button type="button" className="font-semibold text-primary hover:underline" onClick={() => onPickVersion?.(`v${g.v}`)}>v{g.v}</button>
                {info && <StatusTag status={info.status} />}
                <VersionPeople v={info ?? { id: `v${g.v}`, status: "published", note: "", time: "", user: "", from: null }} />
                <span className={`overflow-hidden text-ellipsis whitespace-nowrap ${info?.note ? "text-ink-secondary" : "text-ink-placeholder"}`}>{info?.note || t("ver.noNote")}</span>
              </div>
            )}
            {g.sessions.map((s, i) => <SessionCard key={`${g.v}-${s.actor}-${s.fromMs}-${s.entries[0]?.id ?? i}`} s={s} snapshot={snapshots[g.v]} onUndo={onUndoApplied && undoTarget(g.v) != null ? (selection) => openUndo(g.v, selection) : undefined} open={open.has(`${gi}-${i}`)} onToggle={() => toggle(`${gi}-${i}`)} />)}
          </div>
        );
      })}
      {undo && <UndoChanges key={`${undo.version}|${JSON.stringify(undo.selection)}`} version={undo.version} selection={undo.selection} snapshot={snapshots["sourceVersion" in undo.selection ? undo.selection.sourceVersion ?? undo.version : undo.version]} blocked={undoBlocked} onClose={() => setUndo(null)} onApplied={(v) => { setUndo(null); setSnapshots({}); setPage(1); setRefresh((n) => n + 1); onUndoApplied?.(v); }} />}
      <div className="mt-3 flex items-center gap-3 text-sm text-ink-placeholder">
        {fetched < total && <Button size="sm" disabled={state === "loading"} onClick={() => setPage((p) => p + 1)}>{t("vlog.more")}</Button>}
        {state === "loading" && <span>{t("ver.working")}</span>}
        <span>{t("auditChange.loaded", { loaded: fetched, total })}</span>
        <span>{t("vlog.footer")}</span>
        <button type="button" className="text-primary hover:underline" onClick={() => onOpenFull(serverWho, scope)}>{t("vlog.openFull")}</button>
      </div>
    </Drawer>
  );
}
